/**
 * UTM Builder Page - UTM campaign link generator
 *
 * Allows users to:
 * - Build UTM-tagged URLs
 * - Copy generated links
 * - Save UTM campaigns to database
 * - View, edit, and delete saved campaigns
 */

import { useState, useCallback, useEffect } from "react";
import { PageHeader } from "../components";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";

// Icons
const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const SaveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface UTMParams {
  campaignName: string;
  url: string;
  source: string;
  medium: string;
  campaign: string;
  content: string;
}

interface SavedCampaign {
  _id: string;
  campaignName: string;
  baseUrl: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  generatedUrl: string;
  createdAt: string;
}

export function UTMBuilder() {
  const [params, setParams] = useState<UTMParams>({
    campaignName: "",
    url: "",
    source: "",
    medium: "",
    campaign: "",
    content: "",
  });
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Saved campaigns state
  const [savedCampaigns, setSavedCampaigns] = useState<SavedCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<SavedCampaign | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<SavedCampaign | null>(null);

  // Fetch saved campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch("/api/utm-campaigns");
      const result = await response.json();
      if (result.success) {
        setSavedCampaigns(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const generateURL = useCallback(() => {
    if (!params.url) return "";

    try {
      const url = new URL(params.url.startsWith("http") ? params.url : `https://${params.url}`);

      if (params.source) url.searchParams.set("utm_source", params.source);
      if (params.medium) url.searchParams.set("utm_medium", params.medium);
      if (params.campaign) url.searchParams.set("utm_campaign", params.campaign);
      if (params.content) url.searchParams.set("utm_content", params.content);

      return url.toString();
    } catch {
      return "";
    }
  }, [params]);

  const generatedURL = generateURL();

  const handleCopy = async () => {
    if (!generatedURL) return;

    try {
      await navigator.clipboard.writeText(generatedURL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleClear = () => {
    setParams({
      campaignName: "",
      url: "",
      source: "",
      medium: "",
      campaign: "",
      content: "",
    });
    setSaveSuccess(false);
    setSaveError(null);
    setEditingCampaign(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!params.campaignName || !params.url || !params.source || !params.medium || !params.campaign) {
      setSaveError("Please fill in all required fields");
      return;
    }

    if (!generatedURL) {
      setSaveError("Please enter a valid URL");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const isEditing = editingCampaign !== null;
      const url = isEditing ? `/api/utm-campaigns/${editingCampaign._id}` : "/api/utm-campaigns";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaignName: params.campaignName,
          baseUrl: params.url,
          utmSource: params.source,
          utmMedium: params.medium,
          utmCampaign: params.campaign,
          utmContent: params.content || undefined,
          generatedUrl: generatedURL,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save campaign");
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Refresh campaigns list
      await fetchCampaigns();

      // Clear form if creating new (not editing)
      if (!isEditing) {
        handleClear();
      } else {
        setEditingCampaign(null);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (campaign: SavedCampaign) => {
    setEditingCampaign(campaign);
    setParams({
      campaignName: campaign.campaignName,
      url: campaign.baseUrl,
      source: campaign.utmSource,
      medium: campaign.utmMedium,
      campaign: campaign.utmCampaign,
      content: campaign.utmContent,
    });
    setSaveError(null);
    setSaveSuccess(false);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (campaign: SavedCampaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;

    setDeletingId(campaignToDelete._id);
    try {
      const response = await fetch(`/api/utm-campaigns/${campaignToDelete._id}`, { method: "DELETE" });
      if (response.ok) {
        await fetchCampaigns();
      }
    } catch (error) {
      console.error("Failed to delete campaign:", error);
    } finally {
      setDeletingId(null);
      setCampaignToDelete(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingCampaign(null);
    handleClear();
  };

  const updateParam = (key: keyof UTMParams, value: string) => {
    setParams((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const isFormValid = params.campaignName && params.url && params.source && params.medium && params.campaign;

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(dateString));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="UTM Builder" subtitle="Generate UTM-tagged campaign links" />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input Form */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <EditIcon />
            {editingCampaign ? "Edit UTM Campaign" : "Create UTM Campaign"}
          </h3>

          {editingCampaign && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-500 flex items-center justify-between">
              <span>Editing: {editingCampaign.campaignName}</span>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-amber-500 hover:text-amber-400"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="space-y-4">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Summer Sale 2025"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={params.campaignName}
                onChange={(e) => updateParam("campaignName", e.target.value)}
              />
            </div>

            {/* Base URL */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Base URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                placeholder="https://"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={params.url}
                onChange={(e) => updateParam("url", e.target.value)}
              />
            </div>

            {/* Platform & Medium - Two columns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Platform (utm_source) */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Platform (utm_source) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Instagram, YouTube, TikTok, LinkedIn"
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={params.source}
                  onChange={(e) => updateParam("source", e.target.value)}
                />
              </div>

              {/* Medium (utm_medium) */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Medium (utm_medium) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="paid or organic"
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={params.medium}
                  onChange={(e) => updateParam("medium", e.target.value)}
                />
              </div>
            </div>

            {/* Campaign & Content - Two columns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Campaign (utm_campaign) */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Campaign (utm_campaign) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., January Webinar, Spring Launch"
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={params.campaign}
                  onChange={(e) => updateParam("campaign", e.target.value)}
                />
              </div>

              {/* Content (utm_content) */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Content (utm_content)
                </label>
                <input
                  type="text"
                  placeholder="e.g., reel_hook_1, story_swipe_v2"
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={params.content}
                  onChange={(e) => updateParam("content", e.target.value)}
                />
              </div>
            </div>

            {/* Separator */}
            <hr className="border-border" />

            {/* Save and Clear Buttons */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !isFormValid}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : saveSuccess ? (
                  <>
                    <CheckIcon />
                    Saved!
                  </>
                ) : (
                  <>
                    <SaveIcon />
                    {editingCampaign ? "Update Campaign" : "Save Campaign"}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleClear}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear All Fields
              </button>
            </div>

            {/* Error Message */}
            {saveError && (
              <p className="text-sm text-red-500">{saveError}</p>
            )}
          </div>
        </div>

        {/* Generated URL */}
        <div className="space-y-6">
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">Generated URL</h3>

            {generatedURL ? (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 break-all text-sm font-mono">
                  {generatedURL}
                </div>

                <button
                  type="button"
                  onClick={handleCopy}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                >
                  {copied ? (
                    <>
                      <CheckIcon />
                      Copied!
                    </>
                  ) : (
                    <>
                      <CopyIcon />
                      Copy URL
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-8 text-center">
                <p className="text-muted-foreground">
                  Fill in the fields on the left to generate your UTM-tagged URL
                </p>
              </div>
            )}
          </div>

          {/* UTM Parameter Reference */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">UTM Parameter Guide</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-blue-500">utm_source</span>
                <p className="text-muted-foreground">
                  The platform the traffic comes from — Instagram, YouTube, TikTok, LinkedIn, etc.
                </p>
              </div>
              <div>
                <span className="font-medium text-green-500">utm_medium</span>
                <p className="text-muted-foreground">
                  Whether the traffic is <strong>paid</strong> or <strong>organic</strong>.
                </p>
              </div>
              <div>
                <span className="font-medium text-purple-500">utm_campaign</span>
                <p className="text-muted-foreground">
                  The webinar, launch, or promotion you're running (e.g., january_webinar, spring_launch).
                </p>
              </div>
              <div>
                <span className="font-medium text-red-500">utm_content</span>
                <p className="text-muted-foreground">
                  The specific content piece — the reel, story, post, or ad creative driving the click.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Campaigns Table */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
            <ListIcon />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Saved Campaigns</h2>
            <p className="text-sm text-muted-foreground">
              {savedCampaigns.length} campaign{savedCampaigns.length !== 1 ? "s" : ""} saved
            </p>
          </div>
        </div>

        {loadingCampaigns ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : savedCampaigns.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No campaigns saved yet. Create one above!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Campaign</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Source</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Medium</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Campaign</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">URL</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Created</th>
                  <th className="text-center p-4 font-medium text-muted-foreground text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedCampaigns.map((campaign, index) => (
                  <tr
                    key={campaign._id}
                    className={`border-b border-border ${
                      index % 2 === 0 ? "bg-background" : "bg-muted/20"
                    } hover:bg-muted/40 transition-colors`}
                  >
                    <td className="p-4">
                      <div className="font-medium">{campaign.campaignName}</div>
                    </td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded text-xs font-medium">
                        {campaign.utmSource}
                      </span>
                    </td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 bg-green-500/10 text-green-500 rounded text-xs font-medium">
                        {campaign.utmMedium}
                      </span>
                    </td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 bg-purple-500/10 text-purple-500 rounded text-xs font-medium">
                        {campaign.utmCampaign}
                      </span>
                    </td>
                    <td className="p-4 text-sm max-w-xs">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-muted-foreground text-xs font-mono">
                          {campaign.generatedUrl.length > 50
                            ? `${campaign.generatedUrl.slice(0, 50)}...`
                            : campaign.generatedUrl}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopyUrl(campaign.generatedUrl)}
                          className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                          title="Copy URL"
                        >
                          {copiedUrl === campaign.generatedUrl ? (
                            <CheckIcon />
                          ) : (
                            <CopyIcon />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(campaign)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                          title="Edit"
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(campaign)}
                          disabled={deletingId === campaign._id}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-500 disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === campaign._id ? (
                            <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                          ) : (
                            <TrashIcon />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Campaign"
        itemName={campaignToDelete?.campaignName}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
